import { UserInterface, SidePanelInterface, RootState } from '../types';
import SidePanel from './SidePanel';
import { Box, CircularProgress, FormGroup, Typography, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import { InputField, Label, ModalButton, ModalTitle, DangerButton } from './SharedComponents';
import apiClient from '../services/apiClient';
import moment from 'moment';
import notify from '../services/notify';
import ConfirmationModal from './ConfirmationModal';
import { useSelector } from 'react-redux';

// Extended interface for shop users that includes ownership and other shop-specific properties
interface ShopUserInterface extends UserInterface {
	is_owner?: boolean | number;
	type?: 'user' | 'invitation';
	date_added?: string;
}

interface UserModalProp extends Omit<SidePanelInterface, 'children'> {
	user?: ShopUserInterface | null;
	onUserRemoved?: () => void; // Callback for when user is removed
}

export default function UserModal(props: UserModalProp) {
	const { user, handleHidePanel, onUserRemoved, ...sidePanelProps } = props;

	// Get current user from Redux to check ownership
	const currentUser = useSelector((state: RootState) => state.auth.user);
	const isOwner = currentUser?.is_owner === 1 || currentUser?.is_owner === true;

	const [userEmail, setUserEmail] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isRemoving, setIsRemoving] = useState<boolean>(false);
	const [isNewUser, setIsNewUser] = useState<boolean>(true);
	const [showRemoveConfirmation, setShowRemoveConfirmation] = useState<boolean>(false);

	useEffect(() => {
		if (user) {
			setUserEmail(user.email || '');
			setIsNewUser(false);
		} else {
			setUserEmail('');
			setIsNewUser(true);
		}
	}, [user]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		if (name === 'email') {
			setUserEmail(value);
		}
	};

	const hidePanel = (user: ShopUserInterface | null = null) => {
		setUserEmail('');
		if (user) {
			handleHidePanel(user);
		} else {
			handleHidePanel();
		}
	};

	const createUser = (email: string) => {
		return apiClient.post('/invitations', { email });
	};

	const removeUser = (userID: string) => {
		return apiClient.delete(`/shops/users/${userID}`);
	};

	const resendInvitation = (userID: string) => {
		return apiClient.post(`/users/${userID}/resend-invitation`);
	};

	const isFieldsValid = () => {
		if (userEmail.length === 0) {
			notify('error', 'Email is required');
			return false;
		}
		// Basic email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(userEmail)) {
			notify('error', 'Invalid email address');
			return false;
		}
		return true;
	};

	const handleSendInvitation = () => {
		if (!isFieldsValid()) return;

		setIsLoading(true);
		createUser(userEmail)
			.then((result: any) => {
				console.log('invitation sent', result);
				setIsLoading(false);
				hidePanel(result.data);
			})
			.catch((error) => {
				console.error('Error sending invitation:', error);
				setIsLoading(false);
				notify('error', 'Failed to send invitation. Please try again.');
			});
	};

	const handleResendInvitation = () => {
		if (!user?.id) return;

		setIsLoading(true);
		resendInvitation(user.id)
			.then((result: any) => {
				console.log('invitation resent', result);
				setIsLoading(false);
				hidePanel(result.data);
			})
			.catch((error) => {
				console.error('Error resending invitation:', error);
				setIsLoading(false);
				notify('error', 'Failed to resend invitation. Please try again.');
			});
	};

	const handleRemoveUser = () => {
		setShowRemoveConfirmation(true);
	};

	const confirmRemoveUser = () => {
		if (!user?.id) return;

		setIsRemoving(true);
		removeUser(user.id.toString())
			.then(() => {
				console.log('user removed');
				setIsRemoving(false);
				setShowRemoveConfirmation(false);
				notify('success', 'User removed from shop successfully');
				// Call the callback to signal user was removed
				if (onUserRemoved) {
					onUserRemoved();
				}
				hidePanel();
			})
			.catch((error) => {
				console.error('Error removing user:', error);
				setIsRemoving(false);
				setShowRemoveConfirmation(false);
				notify('error', error.response?.data?.message || 'Failed to remove user. Please try again.');
			});
	};

	const cancelRemoveUser = () => {
		setShowRemoveConfirmation(false);
	};

	// Check if current user has permission to manage users (only shop owners can manage users)
	const canManageUsers = isOwner;
	// Check if this is an accepted user (not a pending invitation)
	const hasAccepted = user?.type === 'user';

	// Check if user is trying to remove themselves or another owner
	const isTargetOwner = user?.is_owner === true || user?.is_owner === 1;
	const canRemoveUser = canManageUsers && !isTargetOwner && hasAccepted;

	return (
		<SidePanel {...sidePanelProps} handleHidePanel={() => hidePanel()}>
			<ModalTitle>{isNewUser ? 'Invite User' : 'User Details'}</ModalTitle>

			{isNewUser ? (
				<>
					<Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
						Enter the email address of the person you'd like to invite to your shop. They will receive an
						invitation email with instructions to get started.
					</Typography>
					<FormGroup sx={{ flex: 8, mb: 3 }}>
						<Label>Email Address</Label>
						<InputField
							type="email"
							name="email"
							value={userEmail}
							onChange={handleChange}
							placeholder="Enter user's email address"
						/>
					</FormGroup>
					<ModalButton onClick={handleSendInvitation}>
						{isLoading ? <CircularProgress size={15} color="inherit" /> : 'Send Invitation'}
					</ModalButton>
				</>
			) : (
				<>
					<Box sx={{ mb: 4 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
							{hasAccepted ? 'User Details' : 'Invitation Details'}
						</Typography>
						{hasAccepted && user?.firstname && (
							<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
								<Typography variant="body2" sx={{ color: 'text.secondary' }}>
									Name:
								</Typography>
								<Typography variant="body2">
									{user.firstname} {user.surname}
								</Typography>
							</Box>
						)}
						<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
							<Typography variant="body2" sx={{ color: 'text.secondary' }}>
								Email:
							</Typography>
							<Typography variant="body2">{user?.email}</Typography>
						</Box>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
							<Typography variant="body2" sx={{ color: 'text.secondary' }}>
								{hasAccepted ? 'Added on:' : 'Invited on:'}
							</Typography>
							<Typography variant="body2">
								{user?.date_created ? moment(user.date_created).format('MMMM Do, YYYY') : 'N/A'}
							</Typography>
						</Box>
						{hasAccepted && (
							<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
								<Typography variant="body2" sx={{ color: 'text.secondary' }}>
									Role:
								</Typography>
								<Typography variant="body2">{isTargetOwner ? 'Owner' : 'User'}</Typography>
							</Box>
						)}
						<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
							<Typography variant="body2" sx={{ color: 'text.secondary' }}>
								Status:
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 'medium',
									color: hasAccepted ? 'success.main' : 'warning.main',
								}}
							>
								{hasAccepted ? 'Active' : 'Pending'}
							</Typography>
						</Box>
					</Box>

					{!hasAccepted && (
						<>
							<Divider sx={{ mb: 3 }} />
							<Typography variant="body2" sx={{ mb: 2 }}>
								This user hasn't accepted their invitation yet. You can resend the invitation if needed.
							</Typography>
							<ModalButton onClick={handleResendInvitation} disabled={isLoading || !canManageUsers}>
								{isLoading ? <CircularProgress size={15} color="inherit" /> : 'Resend Invitation'}
							</ModalButton>
						</>
					)}

					{hasAccepted && canRemoveUser && (
						<>
							<Divider sx={{ mb: 3 }} />
							<Typography variant="body2" sx={{ mb: 2, color: 'error.main' }}>
								Danger Zone
							</Typography>
							<Typography variant="body2" sx={{ mb: 3 }}>
								Removing this user will revoke their access to your shop immediately.
							</Typography>
							<DangerButton onClick={handleRemoveUser} disabled={isRemoving}>
								{isRemoving ? <CircularProgress size={15} color="inherit" /> : 'Remove User'}
							</DangerButton>
						</>
					)}

					{hasAccepted && isTargetOwner && (
						<>
							<Divider sx={{ mb: 3 }} />
							<Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
								Shop Owner
							</Typography>
							<Typography variant="body2" sx={{ color: 'text.secondary' }}>
								Shop owners cannot be removed from the shop. To remove a shop owner, transfer ownership
								to another user first.
							</Typography>
						</>
					)}
				</>
			)}

			<ConfirmationModal
				isOpen={showRemoveConfirmation}
				title="Remove User"
				message={`Are you sure you want to remove ${
					user?.firstname ? `${user.firstname} ${user.surname}` : user?.email
				} from your shop? This will immediately revoke their access and cannot be undone.`}
				confirmText="Remove User"
				cancelText="Cancel"
				onConfirm={confirmRemoveUser}
				onCancel={cancelRemoveUser}
				isLoading={isRemoving}
				variant="danger"
			/>
		</SidePanel>
	);
}
